/**
 * Animated GIFs, a frame at a time. Image I/O in Swift reports a GIF's
 * frames and delays (Sources/ImageIO/GIF.swift) and draws frame `n` as a
 * recipe whose `frame` is `n`; the page draws that frame here: decoded (LZW)
 * and composed over the frames before it as the browser composes them when
 * it animates a GIF itself. The canvas starts clear, a frame's transparent
 * pixels leave what is under them, and a frame's disposal applies before
 * the next one is drawn: none or leave in place (0, 1), clear its rectangle
 * (2: the background, which browsers draw clear), or restore what was under
 * it (3). Frames are composed in order once and kept, so a loop is decoded
 * once (docs/ops-protocol.md, "Animated GIFs").
 */

interface FrameRecord {
  x: number;
  y: number;
  width: number;
  height: number;
  interlaced: boolean;
  /** RGB triples of the frame's own color table, else the global one. */
  colors: Uint8Array | null;
  transparent: number | null;
  disposal: number;
  /** Delay before the next frame, in hundredths of a second. */
  delay: number;
  minCodeSize: number;
  /** The image data's sub-blocks, joined. */
  data: Uint8Array;
}

/** A GIF's frames, composed on demand. */
export class GifDecoder {
  readonly width: number;
  readonly height: number;
  private readonly records: FrameRecord[];
  /** Composed frames so far, straight RGBA. */
  private readonly composed: Uint8ClampedArray<ArrayBuffer>[] = [];
  /** The canvas as the next frame will find it (the last frame's disposal applied). */
  private canvas: Uint8ClampedArray<ArrayBuffer>;

  private constructor(width: number, height: number, records: FrameRecord[]) {
    this.width = width;
    this.height = height;
    this.records = records;
    this.canvas = new Uint8ClampedArray(width * height * 4);
  }

  /** The decoder for a GIF file; null when `bytes` is not one, or has no frame. */
  static parse(bytes: Uint8Array): GifDecoder | null {
    const signature = String.fromCharCode(...bytes.subarray(0, 6));
    if (bytes.length < 13 || (signature !== 'GIF87a' && signature !== 'GIF89a')) return null;
    const u16 = (at: number): number => (bytes[at] ?? 0) | ((bytes[at + 1] ?? 0) << 8);
    const width = u16(6);
    const height = u16(8);
    if (width === 0 || height === 0) return null;
    const screen = bytes[10]!;
    let at = 13;
    let globalColors: Uint8Array | null = null;
    if (screen & 0x80) {
      const size = 3 << ((screen & 0x07) + 1);
      globalColors = bytes.subarray(at, at + size);
      at += size;
    }
    /** The sub-blocks starting at `start`, joined, and the index after them. */
    const subBlocks = (start: number): { data: Uint8Array; next: number } => {
      const parts: Uint8Array[] = [];
      let total = 0;
      let index = start;
      while (index < bytes.length) {
        const size = bytes[index]!;
        index += 1;
        if (size === 0) break;
        const part = bytes.subarray(index, Math.min(bytes.length, index + size));
        parts.push(part);
        total += part.length;
        index += size;
      }
      const data = new Uint8Array(total);
      let offset = 0;
      for (const part of parts) {
        data.set(part, offset);
        offset += part.length;
      }
      return { data, next: index };
    };

    const records: FrameRecord[] = [];
    let transparent: number | null = null;
    let disposal = 0;
    let delay = 0;
    while (at < bytes.length) {
      const block = bytes[at]!;
      if (block === 0x21) {
        const label = bytes[at + 1];
        if (label === 0xf9 && (bytes[at + 2] ?? 0) >= 4) {
          const packed = bytes[at + 3] ?? 0;
          disposal = (packed >> 2) & 0x07;
          delay = u16(at + 4);
          transparent = packed & 0x01 ? (bytes[at + 6] ?? 0) : null;
        }
        at = subBlocks(at + 2).next;
      } else if (block === 0x2c) {
        if (at + 10 > bytes.length) break;
        const packed = bytes[at + 9]!;
        let index = at + 10;
        let colors = globalColors;
        if (packed & 0x80) {
          const size = 3 << ((packed & 0x07) + 1);
          colors = bytes.subarray(index, index + size);
          index += size;
        }
        const minCodeSize = bytes[index] ?? 0;
        const { data, next } = subBlocks(index + 1);
        records.push({
          x: u16(at + 1),
          y: u16(at + 3),
          width: u16(at + 5),
          height: u16(at + 7),
          interlaced: (packed & 0x40) !== 0,
          colors,
          transparent,
          disposal,
          delay,
          minCodeSize,
          data,
        });
        transparent = null;
        disposal = 0;
        delay = 0;
        at = next;
      } else {
        // The trailer (0x3b), or not a block: the file ends here.
        break;
      }
    }
    return records.length > 0 ? new GifDecoder(width, height, records) : null;
  }

  get frameCount(): number {
    return this.records.length;
  }

  /** Frame `index`'s delay in hundredths of a second, as the file says. */
  delay(index: number): number {
    return this.records[index]?.delay ?? 0;
  }

  /** Frame `index` composed over the ones before it: straight RGBA, `width` × `height`. */
  frame(index: number): Uint8ClampedArray<ArrayBuffer> | null {
    if (index < 0 || index >= this.records.length) return null;
    while (this.composed.length <= index) this.composeNext();
    return this.composed[index]!;
  }

  private composeNext(): void {
    const record = this.records[this.composed.length]!;
    const { width, height } = this;
    const before = record.disposal === 3 ? this.canvas.slice() : null;
    const pixels = record.width * record.height;
    const { indices, count } = lzwDecode(record.minCodeSize, record.data, pixels);
    const colors = record.colors;
    const canvas = this.canvas;
    for (let i = 0; i < count; i += 1) {
      const row = interlacedRow(Math.floor(i / record.width), record.height, record.interlaced);
      const x = record.x + (i % record.width);
      const y = record.y + row;
      if (x >= width || y >= height) continue;
      const color = indices[i]!;
      if (color === record.transparent) continue;
      const offset = (y * width + x) * 4;
      if (colors && color * 3 + 2 < colors.length) {
        canvas[offset] = colors[color * 3]!;
        canvas[offset + 1] = colors[color * 3 + 1]!;
        canvas[offset + 2] = colors[color * 3 + 2]!;
      } else {
        canvas[offset] = canvas[offset + 1] = canvas[offset + 2] = 0;
      }
      canvas[offset + 3] = 255;
    }
    this.composed.push(canvas.slice());
    // The disposal readies the canvas for the next frame.
    if (record.disposal === 2) {
      for (let y = record.y; y < Math.min(height, record.y + record.height); y += 1) {
        const start = (y * width + record.x) * 4;
        const end = (y * width + Math.min(width, record.x + record.width)) * 4;
        if (end > start) canvas.fill(0, start, end);
      }
    } else if (before) {
      this.canvas = before;
    }
  }
}

/** The row an interlaced image's `n`th decoded row belongs on (passes of 8, 8, 4 and 2). */
function interlacedRow(n: number, height: number, interlaced: boolean): number {
  if (!interlaced) return n;
  const passes: [number, number][] = [
    [0, 8],
    [4, 8],
    [2, 4],
    [1, 2],
  ];
  let remaining = n;
  for (const [start, step] of passes) {
    const rows = start < height ? Math.ceil((height - start) / step) : 0;
    if (remaining < rows) return start + remaining * step;
    remaining -= rows;
  }
  return height;
}

/**
 * GIF's variable-width LZW: color indices for up to `pixels` pixels, and how
 * many the data held (a file cut short leaves the rest undrawn).
 */
export function lzwDecode(minCodeSize: number, data: Uint8Array, pixels: number): { indices: Uint8Array; count: number } {
  const indices = new Uint8Array(pixels);
  if (minCodeSize < 1 || minCodeSize > 11) return { indices, count: 0 };
  const clear = 1 << minCodeSize;
  const end = clear + 1;
  const prefix = new Int16Array(4096);
  const suffix = new Uint8Array(4096);
  const stack = new Uint8Array(4097);
  for (let code = 0; code < clear; code += 1) {
    prefix[code] = -1;
    suffix[code] = code;
  }
  let codeSize = minCodeSize + 1;
  let codeMask = (1 << codeSize) - 1;
  let next = end + 1;
  let previous = -1;
  let first = 0;
  let datum = 0;
  let bits = 0;
  let position = 0;
  let count = 0;
  while (count < pixels) {
    while (bits < codeSize && position < data.length) {
      datum |= data[position]! << bits;
      position += 1;
      bits += 8;
    }
    if (bits < codeSize) break;
    let code = datum & codeMask;
    datum >>>= codeSize;
    bits -= codeSize;
    if (code === clear) {
      codeSize = minCodeSize + 1;
      codeMask = (1 << codeSize) - 1;
      next = end + 1;
      previous = -1;
      continue;
    }
    if (code === end) break;
    if (previous === -1) {
      if (code >= clear) break;
      indices[count] = code;
      count += 1;
      previous = code;
      first = code;
      continue;
    }
    if (code > next) break;
    const current = code;
    let top = 0;
    if (code === next) {
      // The code being defined: the previous string and its own first index.
      stack[top] = first;
      top += 1;
      code = previous;
    }
    while (code >= clear) {
      stack[top] = suffix[code]!;
      top += 1;
      code = prefix[code]!;
    }
    first = suffix[code]!;
    stack[top] = first;
    top += 1;
    if (next < 4096) {
      prefix[next] = previous;
      suffix[next] = first;
      next += 1;
      if ((next & codeMask) === 0 && next < 4096) {
        codeSize += 1;
        codeMask = (1 << codeSize) - 1;
      }
    }
    previous = current;
    while (top > 0 && count < pixels) {
      top -= 1;
      indices[count] = stack[top]!;
      count += 1;
    }
  }
  return { indices, count };
}
